import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, maskCPF } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeftRight, Search, Send, Inbox, History, Check, X, AlertTriangle,
  Users, Loader2, ShoppingBag, Zap,
} from "lucide-react";

const UNITS = [
  { key: "CRIS_LINGERIE", label: "Cris Lingerie", icon: ShoppingBag },
  { key: "ELETROS", label: "Eletros", icon: Zap },
];
const STATUS_META = {
  PENDING: { label: "Pendente", cls: "bg-amber-100 text-amber-700" },
  ACCEPTED: { label: "Aceita", cls: "bg-emerald-100 text-emerald-700" },
  REJECTED: { label: "Recusada", cls: "bg-red-100 text-red-700" },
  CANCELLED: { label: "Cancelada", cls: "bg-gray-100 text-gray-600" },
};
const unitLabel = (u) => (UNITS.find((x) => x.key === u) || {}).label || u;

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.PENDING;
  return <span data-testid={`transfer-status-${status}`} className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${m.cls}`}>{m.label}</span>;
}

// ---------------------------------------------------------------- Confirm modal
function ConfirmModal({ open, title, children, confirmLabel, confirmCls, onConfirm, onClose, busy, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div data-testid={testid} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl">
        <div className="flex items-start gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-grad text-white"><ArrowLeftRight size={20} /></div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-brand-dark">{title}</h3>
            <div className="mt-2 text-sm text-[#6B5A69]">{children}</div>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button data-testid={`${testid}-cancel`} onClick={onClose} disabled={busy}
            className="flex-1 rounded-xl border border-border bg-white py-2.5 text-sm font-semibold text-brand-dark hover:bg-muted">Voltar</button>
          <button data-testid={`${testid}-confirm`} onClick={onConfirm} disabled={busy}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white ${confirmCls || "bg-brand-grad"}`}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : null}{confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- New transfer
function NewTransfer({ onDone }) {
  const [unit, setUnit] = useState("CRIS_LINGERIE");
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState({});
  const [resellers, setResellers] = useState([]);
  const [dest, setDest] = useState("");
  const [destQ, setDestQ] = useState("");
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadResellers = useCallback(async (u) => {
    try {
      const r = await api.get(`/transfers/eligible-resellers?business_unit=${u}`);
      setResellers(r.data); setDest("");
    } catch { setResellers([]); }
  }, []);

  const loadClients = useCallback(async (u, term) => {
    try {
      if (u === "ELETROS") {
        const r = await api.get(`/eletros/clients/search?q=${encodeURIComponent(term || "")}`);
        setClients(r.data.map((c) => ({ id: c.id, name: c.name, cpf: c.cpf, phone: c.phone })));
      } else {
        const r = await api.get(`/clients${term ? `?q=${encodeURIComponent(term)}` : ""}`);
        setClients(r.data.map((c) => ({ id: c.id, name: c.name, cpf: c.cpf, phone: c.phone })));
      }
    } catch { setClients([]); }
  }, []);

  useEffect(() => { loadResellers(unit); setSelected({}); loadClients(unit, ""); }, [unit, loadResellers, loadClients]);
  useEffect(() => { const t = setTimeout(() => loadClients(unit, q), 300); return () => clearTimeout(t); }, [q, unit, loadClients]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const destName = (resellers.find((r) => r.id === dest) || {}).name;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/transfers", { business_unit: unit, to_reseller_id: dest, client_ids: selectedIds, note: note || null });
      const ok = r.data.created, fail = r.data.total - r.data.created;
      if (ok > 0) toast.success(`${ok} solicitação(ões) enviada(s)${fail ? `, ${fail} com aviso` : ""}.`);
      (r.data.results || []).filter((x) => !x.ok).forEach((x) => toast.error(`${x.name || x.client_id}: ${x.error}`));
      setConfirm(false); setSelected({}); setNote(""); onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao solicitar transferência.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="transfer-new" className="space-y-5">
      {/* Unit */}
      <div className="flex flex-wrap gap-2">
        {UNITS.map((u) => (
          <button key={u.key} data-testid={`transfer-unit-${u.key}`} onClick={() => setUnit(u.key)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${unit === u.key ? "bg-brand-grad text-white" : "border border-border bg-white text-[#7A6A78] hover:bg-muted"}`}>
            <u.icon size={15} /> {u.label}
          </button>
        ))}
      </div>

      {/* Destination */}
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#9A8A98]">Revendedora de destino</label>
        {resellers.length > 8 && (
          <div className="relative mb-2">
            <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9A8A98]" />
            <input data-testid="transfer-dest-search" value={destQ} onChange={(e) => setDestQ(e.target.value)} placeholder="Filtrar revendedora..."
              className="h-11 w-full rounded-2xl border border-border bg-white pl-10 pr-4 text-sm outline-none focus:border-primary" />
          </div>
        )}
        <select data-testid="transfer-dest-select" value={dest} onChange={(e) => setDest(e.target.value)}
          className="h-12 w-full rounded-2xl border border-border bg-white px-4 text-sm outline-none focus:border-primary">
          <option value="">Selecione...</option>
          {resellers.filter((r) => !destQ || (r.name || "").toLowerCase().includes(destQ.toLowerCase()))
            .map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {resellers.length === 0 && <div className="mt-1 text-xs text-[#9A8A98]">Nenhuma revendedora elegível para esta unidade.</div>}
      </div>

      {/* Clients search + list */}
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-[#9A8A98]">Clientes ({selectedIds.length} selecionado(s))</label>
        <div className="relative mb-2">
          <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9A8A98]" />
          <input data-testid="transfer-client-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, CPF ou telefone..."
            className="h-11 w-full rounded-2xl border border-border bg-white pl-10 pr-4 text-sm outline-none focus:border-primary" />
        </div>
        <div className="max-h-72 space-y-1.5 overflow-y-auto rounded-2xl border border-border bg-white p-2">
          {clients.length === 0 && <div className="py-8 text-center text-sm text-[#9A8A98]">Nenhum cliente na sua carteira desta unidade.</div>}
          {clients.map((c) => (
            <label key={c.id} data-testid={`transfer-client-${c.id}`}
              className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 hover:bg-muted">
              <input type="checkbox" data-testid={`transfer-client-check-${c.id}`} checked={!!selected[c.id]}
                onChange={(e) => setSelected((s) => ({ ...s, [c.id]: e.target.checked }))}
                className="h-4 w-4 accent-[#6A1B9A]" />
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-grad text-xs font-semibold text-white">{(c.name || "?")[0]?.toUpperCase()}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-brand-dark">{c.name}</span>
                <span className="block text-xs text-[#9A8A98]">{maskCPF(c.cpf)}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <textarea data-testid="transfer-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder="Observação (opcional)"
        className="w-full rounded-2xl border border-border bg-white p-3 text-sm outline-none focus:border-primary" />

      <button data-testid="transfer-submit-btn" disabled={!dest || selectedIds.length === 0}
        onClick={() => setConfirm(true)}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-grad py-3 text-sm font-semibold text-white disabled:opacity-50">
        <Send size={16} /> Solicitar transferência
      </button>

      <ConfirmModal open={confirm} testid="transfer-confirm-modal" busy={busy}
        title="Confirmar transferência" confirmLabel="Confirmar e solicitar" onClose={() => setConfirm(false)} onConfirm={submit}>
        Você vai transferir <b>{selectedIds.length} cliente(s)</b> da unidade <b>{unitLabel(unit)}</b> para <b>{destName}</b>.
        <div className="mt-3 rounded-xl bg-amber-50 p-3 text-[13px] text-amber-800">
          <AlertTriangle size={14} className="mr-1 inline" />
          As <b>vendas anteriores continuam suas</b> (parcelas, cobranças, recebimentos e comissões permanecem com você até a quitação). O destino só poderá fazer <b>novas vendas</b> após aceitar.
        </div>
      </ConfirmModal>
    </div>
  );
}

// ---------------------------------------------------------------- List views
function TransferCard({ t, side, onAction }) {
  const other = side === "received" ? t.from_reseller_name : t.to_reseller_name;
  const otherLabel = side === "received" ? "De" : "Para";
  return (
    <div data-testid={`transfer-card-${t.id}`} className="rounded-2xl border border-border bg-white p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-brand-dark">{t.client_name}</div>
          <div className="mt-0.5 text-xs text-[#9A8A98]">{maskCPF(t.client_cpf)} · {unitLabel(t.business_unit)}</div>
          <div className="mt-1 text-xs text-[#6B5A69]">{otherLabel}: <b>{other || "-"}</b></div>
          {t.note && <div className="mt-1 text-xs italic text-[#9A8A98]">"{t.note}"</div>}
          {t.reason && <div className="mt-1 text-xs text-red-600">Motivo: {t.reason}</div>}
        </div>
        <StatusBadge status={t.status} />
      </div>
      {t.status === "PENDING" && (
        <div className="mt-3 flex gap-2">
          {side === "received" ? (
            <>
              <button data-testid={`transfer-accept-${t.id}`} onClick={() => onAction("accept", t)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 py-2 text-xs font-semibold text-white hover:bg-emerald-700"><Check size={14} /> Aceitar</button>
              <button data-testid={`transfer-reject-${t.id}`} onClick={() => onAction("reject", t)}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-red-200 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"><X size={14} /> Recusar</button>
            </>
          ) : (
            <button data-testid={`transfer-cancel-${t.id}`} onClick={() => onAction("cancel", t)}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-border py-2 text-xs font-semibold text-[#7A6A78] hover:bg-muted"><X size={14} /> Cancelar solicitação</button>
          )}
        </div>
      )}
    </div>
  );
}

function ListView({ endpoint, side, refreshKey }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(endpoint === "received" ? "PENDING" : "");
  const [q, setQ] = useState("");
  const [action, setAction] = useState(null); // {kind, t}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("client", q);
    const r = await api.get(`/transfers/${endpoint}?${params.toString()}`);
    setItems(r.data);
  }, [endpoint, status, q]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doAction = async () => {
    setBusy(true);
    try {
      const { kind, t } = action;
      if (kind === "accept") await api.post(`/transfers/${t.id}/accept`);
      else if (kind === "reject") await api.post(`/transfers/${t.id}/reject`, { reason: reason || null });
      else if (kind === "cancel") await api.post(`/transfers/${t.id}/cancel`);
      toast.success("Feito!");
      setAction(null); setReason(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível concluir.");
      setAction(null); load();
    } finally { setBusy(false); }
  };

  const STATUSES = endpoint === "history"
    ? [["", "Todas"], ["ACCEPTED", "Aceitas"], ["REJECTED", "Recusadas"], ["CANCELLED", "Canceladas"]]
    : [["", "Todas"], ["PENDING", "Pendentes"], ["ACCEPTED", "Aceitas"], ["REJECTED", "Recusadas"], ["CANCELLED", "Canceladas"]];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9A8A98]" />
          <input data-testid={`transfer-${endpoint}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente..."
            className="h-10 w-full rounded-full border border-border bg-white pl-10 pr-4 text-sm outline-none focus:border-primary" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUSES.map(([k, l]) => (
            <button key={k} data-testid={`transfer-${endpoint}-filter-${k || "all"}`} onClick={() => setStatus(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${status === k ? "bg-brand-grad text-white" : "border border-border bg-white text-[#7A6A78]"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {items.map((t) => <TransferCard key={t.id} t={t} side={side} onAction={(kind, tt) => setAction({ kind, t: tt })} />)}
      </div>
      {items.length === 0 && <div className="rounded-2xl border border-dashed border-border bg-white py-12 text-center text-sm text-[#9A8A98]">Nenhuma transferência.</div>}

      <ConfirmModal open={action?.kind === "accept"} testid="transfer-accept-modal" busy={busy}
        title="Aceitar transferência" confirmLabel="Aceitar cliente" confirmCls="bg-emerald-600"
        onClose={() => setAction(null)} onConfirm={doAction}>
        Você passará a atender <b>{action?.t?.client_name}</b> e poderá fazer <b>novas vendas</b>.
        <div className="mt-3 rounded-xl bg-amber-50 p-3 text-[13px] text-amber-800">
          <AlertTriangle size={14} className="mr-1 inline" />
          As <b>vendas anteriores continuam com a revendedora de origem</b>. Você não recebe comissão nem assume as cobranças dessas vendas.
        </div>
      </ConfirmModal>

      <ConfirmModal open={action?.kind === "reject"} testid="transfer-reject-modal" busy={busy}
        title="Recusar transferência" confirmLabel="Recusar" confirmCls="bg-red-600"
        onClose={() => setAction(null)} onConfirm={doAction}>
        Recusar a transferência de <b>{action?.t?.client_name}</b>?
        <input data-testid="transfer-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (opcional)"
          className="mt-3 h-10 w-full rounded-xl border border-border bg-white px-3 text-sm outline-none focus:border-primary" />
      </ConfirmModal>

      <ConfirmModal open={action?.kind === "cancel"} testid="transfer-cancel-modal" busy={busy}
        title="Cancelar solicitação" confirmLabel="Cancelar solicitação" confirmCls="bg-gray-600"
        onClose={() => setAction(null)} onConfirm={doAction}>
        Cancelar a solicitação de transferência de <b>{action?.t?.client_name}</b>?
      </ConfirmModal>
    </div>
  );
}

// ---------------------------------------------------------------- Page
export default function TransfersPage() {
  useAuth();
  const [tab, setTab] = useState("new");
  const [refreshKey, setRefreshKey] = useState(0);

  const TABS = [
    { key: "new", label: "Transferir", icon: ArrowLeftRight },
    { key: "sent", label: "Enviadas", icon: Send },
    { key: "received", label: "Recebidas", icon: Inbox },
    { key: "history", label: "Histórico", icon: History },
  ];

  return (
    <div data-testid="transfers-page" className="space-y-6">
      <div className="topbar">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-brand">Carteira</div>
          <h1 className="mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-brand-dark">
            <Users size={26} /> Transferência de Clientes
          </h1>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`transfer-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-brand-grad text-white" : "text-[#7A6A78] hover:bg-muted"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "new" && <NewTransfer onDone={() => { setRefreshKey((k) => k + 1); setTab("sent"); }} />}
      {tab === "sent" && <ListView endpoint="sent" side="sent" refreshKey={refreshKey} />}
      {tab === "received" && <ListView endpoint="received" side="received" refreshKey={refreshKey} />}
      {tab === "history" && <ListView endpoint="history" side="history" refreshKey={refreshKey} />}
    </div>
  );
}
